/**
 * "Log out" has to work on this device whether or not the server answers.
 *
 * The Supabase library's signOut asks the server first and, in the version this app uses (2.89),
 * keeps the saved session if that request fails. Measured with the real client:
 *
 *   the request gets no answer, a 500 or a gateway page   an error back, the session still saved
 *   the request is never answered                          signOut never returns
 *   the token has run out and nothing answers              25 seconds, an error, the session still
 *                                                          saved, and the server is never asked
 *
 * The app showed the welcome screen all the same, so the person believed they had logged out and
 * the next person to open the app on that phone was in their account.
 *
 * So: the server is asked, for a few seconds at most. If it ends the session, good: the library
 * has removed the saved copy and told the other tabs. If not, this device forgets the session
 * anyway. A note is left while that is going on, so that closing the app half-way cannot bring
 * the session back: the next start finishes the job before the library reads anything
 * (finishSignOutLeftUnfinished, called from src/supabase.js).
 *
 * If the wait runs out, the library is still waiting on something: its own call is queued behind
 * a request that hangs (the browser check showed one, a read of the signed-in user, in flight when
 * "Log out" was pressed). Left alone, that queued call would run whenever the hung request lets go
 * and sign out whoever is signed in by then. So the caller is told (`gaveUp`) and starts the page
 * afresh, which leaves nothing of the old sign-in waiting.
 *
 * What it cannot do: when the server never hears of it, the session stays alive on the server
 * until it lapses. Nobody holds it any more, because the only copy was on this device.
 *
 * No imports, so the real client can be put through it in scripts/client-tests/logs-out.test.mjs.
 */
export const SIGN_OUT_WAIT_MS = 5000;
export const SIGN_OUT_UNFINISHED = 'cipher.signOutUnfinished';

/** The key the Supabase library saves the session under. A test holds it to the library's own. */
export const savedSessionKey = (supabaseUrl) => `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`;

// Storage can refuse (private browsing, a full disk). Nothing here may throw.
const safely = (work, otherwise) => {
  try {
    return work();
  } catch {
    return otherwise;
  }
};

/** Removes the saved session from this device. True when nothing is left. */
export const forgetSavedSession = (storage, key) =>
  safely(() => {
    storage.removeItem(key);
    storage.removeItem(`${key}-code-verifier`);
    return storage.getItem(key) === null;
  }, false);

const noteSignOutStarted = (storage) => safely(() => storage.setItem(SIGN_OUT_UNFINISHED, String(Date.now())), undefined);

const takeNoteDown = (storage) =>
  safely(() => {
    storage.removeItem(SIGN_OUT_UNFINISHED);
    return storage.getItem(SIGN_OUT_UNFINISHED) === null;
  }, false);

/**
 * At start-up, before the Supabase client is made. If a log-out was cut short, the saved session
 * goes now. The note is taken down first, and if it cannot be, the session is left alone: staying
 * signed in is better than being signed out on every start.
 */
export const finishSignOutLeftUnfinished = (storage, key) => {
  const left = safely(() => storage.getItem(SIGN_OUT_UNFINISHED), null);
  if (left === null) return false;
  if (!takeNoteDown(storage)) return false;
  return forgetSavedSession(storage, key);
};

const GAVE_UP = Symbol('gave up waiting');
const within = (promise, ms) => {
  let timer;
  const gaveUp = new Promise((resolve) => {
    timer = setTimeout(() => resolve(GAVE_UP), ms);
  });
  return Promise.race([promise, gaveUp]).finally(() => clearTimeout(timer));
};

/**
 * Signs out of this device. Never throws, and never takes much longer than `waitMs`.
 * Resolves with { serverTold, forgotten, gaveUp }: whether the server ended the session, whether
 * the saved session is gone from this device (it always should be), and whether the wait ran out
 * with the library still busy, in which case the page should be started afresh.
 */
export async function signOutOf({ client, storage, key, waitMs = SIGN_OUT_WAIT_MS }) {
  noteSignOutStarted(storage);
  let serverTold = false;
  let gaveUp = false;
  try {
    const result = await within(client.auth.signOut(), waitMs);
    gaveUp = result === GAVE_UP;
    serverTold = !gaveUp && !result?.error;
  } catch {
    // The device forgets the session below, whatever went wrong here.
  }
  const forgotten = forgetSavedSession(storage, key);
  takeNoteDown(storage);
  return { serverTold, forgotten, gaveUp };
}
